<template>
    <div class="documents-share">
        <div class="doc-header">
            <div>
                <h2>Documents Share</h2>
                <p class="doc-subtitle">
                    Public reference documents — PNI, insurance policies, terminal info, and more.
                </p>
            </div>
            <a-button type="primary" icon="upload" @click="showUpload = true">
                Upload Document
            </a-button>
        </div>

        <!-- Upload Modal -->
        <a-modal title="Upload Document" v-model="showUpload" :footer="null" @cancel="resetUploadForm">
            <a-form-item label="Category">
                <a-select v-model="uploadCategory" style="width: 100%">
                    <a-select-option v-for="c in categories" :key="c" :value="c">{{ c }}</a-select-option>
                </a-select>
            </a-form-item>
            <a-upload-dragger name="file" multiple :fileList="uploadFileList"
                :action="$store.state.base_url + '/upload'" :headers="headers" @change="handleUploadChange">
                <p class="ant-upload-drag-icon">
                    <a-icon type="inbox" />
                </p>
                <p class="ant-upload-text">Click or drag file(s) to this area to upload</p>
            </a-upload-dragger>
        </a-modal>

        <a-modal :title="previewDoc.name || 'Preview'" v-model="previewVisible" :footer="null" width="90%"
            :bodyStyle="{ height: '75vh', overflow: 'auto', padding: '8px' }" @cancel="closePreview">
            <vue-office-docx v-if="isDocxPreview" :src="previewDoc.url" style="width: 100%; min-height: 100%" />
            <vue-office-excel v-else-if="isExcelPreview" :src="previewDoc.url" style="width: 100%; min-height: 100%" />
            <vue-office-pptx v-else-if="isPptxPreview" :src="previewDoc.url" style="width: 100%; min-height: 100%" />
            <div v-else-if="isEmlPreview" class="eml-preview">
                <a-spin :spinning="emlLoading">
                    <a-alert v-if="emlError" type="error" show-icon :message="emlError" style="margin-bottom: 8px" />
                    <div v-else class="eml-viewer">
                        <div class="eml-subject">{{ emlSubject || "(no subject)" }}</div>
                        <div class="eml-header">
                            <div class="eml-row" v-for="h in emlHeaders" :key="h.key">
                                <div class="eml-key">{{ h.key }}</div>
                                <div class="eml-val">{{ h.value }}</div>
                            </div>
                        </div>
                        <iframe v-if="emlHtml" class="eml-body-frame" :srcdoc="emlHtml" sandbox="allow-same-origin"
                            @load="onEmlFrameLoad" ref="emlFrame"></iframe>
                        <pre v-else class="eml-body">{{ emlText }}</pre>
                    </div>
                </a-spin>
            </div>
            <iframe v-else :src="previewDoc.url" style="width: 100%; height: 100%; border: 0"></iframe>
        </a-modal>

        <!-- Category Tabs -->
        <a-tabs type="card" v-model="activeCategory">
            <a-tab-pane key="All" :tab="`All (${documents.length})`" />
            <a-tab-pane v-for="c in categories" :key="c"
                :tab="`${c} (${documents.filter((d) => d.category === c).length})`" />
        </a-tabs>

        <a-input-search v-model="searchText" placeholder="Search documents…" style="width: 320px; margin-bottom: 12px"
            allow-clear />
        <span v-if="searchText" class="doc-search-hint">
            {{ filteredDocs.length }} result(s) for "{{ searchText }}"
        </span>

        <!-- Document list -->
        <a-spin :spinning="loading">
            <a-collapse v-if="groupedDocs.length" v-model="expandedKeys">
                <a-collapse-panel v-for="grp in groupedDocs" :key="grp.name"
                    :header="`${grp.name} (${grp.items.length})`">
                    <a-list :dataSource="grp.items" itemLayout="horizontal">
                        <a-list-item slot="renderItem" slot-scope="doc">
                            <a-list-item-meta :description="fmtDate(doc.create_at)">
                                <span slot="title">{{ doc.name }}</span>
                            </a-list-item-meta>
                            <template slot="actions">
                                <a href="#" @click.prevent="openPreview(doc)">Preview</a>
                                <a-popconfirm title="Delete this document?" ok-text="Delete" cancel-text="Cancel"
                                    @confirm="doDelete(doc)">
                                    <a href="#" style="color: #f5222d">Delete</a>
                                </a-popconfirm>
                            </template>
                        </a-list-item>
                    </a-list>
                </a-collapse-panel>
            </a-collapse>
            <a-empty v-else description="No documents yet." style="margin-top: 48px" />
        </a-spin>
    </div>
</template>

<script>
import moment from "moment";
import userinfo from "../utils/userinfo.js";
import "@vue-office/docx/lib/v2/index.css";
import "@vue-office/excel/lib/v2/index.css";

const VueOfficeDocxMod = require("@vue-office/docx/lib/v2/vue-office-docx.umd.js");
const VueOfficeExcelMod = require("@vue-office/excel/lib/v2/vue-office-excel.umd.js");
const VueOfficePptxMod = require("@vue-office/pptx/lib/v2/vue-office-pptx.umd.js");
const VueOfficeDocx = VueOfficeDocxMod.default || VueOfficeDocxMod;
const VueOfficeExcel = VueOfficeExcelMod.default || VueOfficeExcelMod;
const VueOfficePptx = VueOfficePptxMod.default || VueOfficePptxMod;

const CATEGORIES = ["PNI", "INSURANCE", "PDA", "PORT", "AGM"];

export default {
    name: "documents_share",
    components: {
        VueOfficeDocx,
        VueOfficeExcel,
        VueOfficePptx,
    },
    data() {
        return {
            headers: {
                access_token: userinfo.get().access_token,
            },
            loading: false,
            documents: [],
            categories: CATEGORIES,
            activeCategory: "All",
            searchText: "",
            expandedKeys: [],
            showUpload: false,
            uploadCategory: CATEGORIES[0],
            uploadFileList: [],
            previewVisible: false,
            previewDoc: {
                name: "",
                url: "",
            },
            emlLoading: false,
            emlError: "",
            emlHeaders: [],
            emlSubject: "",
            emlHtml: "",
            emlText: "",
        };
    },
    computed: {
        previewFileName() {
            return this.previewDoc.name || this.previewDoc.url || "";
        },
        isDocxPreview() {
            return /\.docx?(\?|$)/i.test(this.previewFileName);
        },
        isExcelPreview() {
            return /\.xlsx?(\?|$)/i.test(this.previewFileName);
        },
        isPptxPreview() {
            return /\.pptx?(\?|$)/i.test(this.previewFileName);
        },
        isEmlPreview() {
            return /\.eml(\?|$)/i.test(this.previewFileName);
        },
        filteredDocs() {
            const kw = (this.searchText || "").trim().toLowerCase();
            let docs = this.documents;
            if (kw) {
                docs = docs.filter(
                    (d) =>
                        (d.name || "").toLowerCase().includes(kw) ||
                        (d.category || "").toLowerCase().includes(kw)
                );
            }
            if (this.activeCategory !== "All") {
                docs = docs.filter((d) => d.category === this.activeCategory);
            }
            return docs;
        },
        groupedDocs() {
            const map = new Map();
            this.filteredDocs.forEach((doc) => {
                const cat = doc.category || "";
                if (!map.has(cat)) map.set(cat, []);
                map.get(cat).push(doc);
            });
            return [...map.entries()]
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, items]) => ({ name, items }));
        },
    },
    methods: {
        fetchDocuments() {
            this.loading = true;
            this.$http
                .post(this.$store.state.base_url + "/document/r", {})
                .then((res) => {
                    if (res.data.status) {
                        this.documents = res.data.data || [];
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleUploadChange(info) {
            this.uploadFileList = info.fileList;
            if (info.file.status === "done") {
                const resp = info.file.response;
                if (resp && resp.status && resp.data && resp.data.length) {
                    const path = resp.data[0].path;
                    const url = this.$store.state.site_url + path.replace(/^\//, "");
                    this.$http
                        .post(this.$store.state.base_url + "/document/c", {
                            url,
                            category: this.uploadCategory,
                            name: info.file.name,
                        })
                        .then((res) => {
                            if (res.data.status) {
                                this.$message.success("Document uploaded.");
                                this.fetchDocuments();
                            }
                        });
                }
            } else if (info.file.status === "error") {
                this.$message.error("Upload failed.");
            }
        },
        openPreview(doc) {
            this.previewDoc = {
                name: doc.name || "",
                url: doc.url || "",
            };
            this.previewVisible = true;
            if (this.isEmlPreview) {
                this.loadEmlPreview(this.previewDoc.url);
            } else {
                this.clearEmlState();
            }
        },
        closePreview() {
            this.previewVisible = false;
            this.previewDoc = {
                name: "",
                url: "",
            };
            this.clearEmlState();
        },
        clearEmlState() {
            this.emlLoading = false;
            this.emlError = "";
            this.emlHeaders = [];
            this.emlSubject = "";
            this.emlHtml = "";
            this.emlText = "";
        },
        onEmlFrameLoad() {
            // auto-size the iframe to fit its content so the email reads naturally
            const frame = this.$refs.emlFrame;
            if (!frame || !frame.contentWindow) return;
            try {
                const doc = frame.contentWindow.document;
                const height = doc.documentElement ? doc.documentElement.scrollHeight : 0;
                frame.style.height = Math.max(height + 20, 200) + "px";
            } catch (e) {
                /* ignore cross-origin edge cases */
            }
        },
        async loadEmlPreview(url) {
            this.clearEmlState();
            if (!url) {
                this.emlError = "Invalid EML URL.";
                return;
            }
            this.emlLoading = true;
            try {
                const resp = await fetch(url);
                if (!resp.ok) {
                    throw new Error("Failed to load EML file.");
                }
                const raw = await resp.text();
                const parsed = this.parseEml(raw);
                this.emlHeaders = parsed.headers;
                this.emlSubject = parsed.subject;
                this.emlHtml = parsed.html;
                this.emlText = parsed.text || "(empty message body)";
            } catch (e) {
                this.emlError = (e && e.message) || "Failed to preview EML file.";
            } finally {
                this.emlLoading = false;
            }
        },
        splitHeaderBody(raw) {
            const match = raw.match(/\r?\n\r?\n/);
            const headerPart = match ? raw.slice(0, match.index) : raw;
            const bodyPart = match ? raw.slice(match.index + match[0].length) : "";

            const lines = headerPart.replace(/\r\n/g, "\n").split("\n");
            const unfolded = [];
            lines.forEach((line) => {
                if (/^\s/.test(line) && unfolded.length) {
                    unfolded[unfolded.length - 1] += " " + line.trim();
                } else {
                    unfolded.push(line);
                }
            });

            const headersMap = {};
            unfolded.forEach((line) => {
                const idx = line.indexOf(":");
                if (idx <= 0) return;
                const key = line.slice(0, idx).trim();
                const val = line.slice(idx + 1).trim();
                headersMap[key.toLowerCase()] = val;
            });

            return { headers: headersMap, body: bodyPart };
        },
        parseEml(raw) {
            const { headers: headersMap } = this.splitHeaderBody(raw);

            const shownHeaders = ["From", "To", "Cc", "Date"]
                .map((k) => ({ key: k, value: headersMap[k.toLowerCase()] }))
                .filter((h) => h.value);
            const subject = headersMap["subject"] || "";

            const bodies = this.collectEmlBodies(raw);

            return {
                headers: shownHeaders,
                subject,
                html: bodies.html ? this.sanitizeEmlHtml(bodies.html) : "",
                text: (bodies.text || "").trim(),
            };
        },
        collectEmlBodies(raw) {
            const { headers: headersMap, body } = this.splitHeaderBody(raw);
            const contentType = headersMap["content-type"] || "text/plain";

            if (/multipart\//i.test(contentType)) {
                const boundaryMatch = contentType.match(/boundary\s*=\s*(?:"([^"]+)"|([^;]+))/i);
                const boundary = boundaryMatch ? (boundaryMatch[1] || boundaryMatch[2] || "").trim() : "";
                if (!boundary) return { text: "", html: "" };

                const marker = "--" + boundary;
                const parts = body.split(marker).filter((p) => p && !/^--\s*$/.test(p.trim()));
                let text = "";
                let html = "";
                parts.forEach((part) => {
                    const r = this.collectEmlBodies(part);
                    if (!text && r.text) text = r.text;
                    if (!html && r.html) html = r.html;
                });
                return { text, html };
            }

            const transferEncoding = (headersMap["content-transfer-encoding"] || "").toLowerCase();
            const decoded = this.decodeTransferEncoding(body, transferEncoding);
            if (/text\/html/i.test(contentType)) {
                return { text: "", html: decoded };
            }
            if (/text\/plain/i.test(contentType) || !headersMap["content-type"]) {
                return { text: decoded, html: "" };
            }
            return { text: "", html: "" };
        },
        decodeTransferEncoding(text, encoding) {
            let out = text || "";
            if (encoding === "base64") {
                try {
                    const normalized = out.replace(/\s+/g, "");
                    out = decodeURIComponent(escape(window.atob(normalized)));
                } catch (e) {
                    return text;
                }
            } else if (encoding === "quoted-printable") {
                out = out
                    .replace(/=\r?\n/g, "")
                    .replace(/=([0-9A-F]{2})/gi, (m, hex) => String.fromCharCode(parseInt(hex, 16)));
            }
            return out;
        },
        sanitizeEmlHtml(html) {
            // strip scripts, inline event handlers and javascript: URLs before rendering in the iframe
            let out = html.replace(/<script[\s\S]*?<\/script>/gi, "");
            out = out.replace(/<iframe[\s\S]*?<\/iframe>/gi, "");
            out = out.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
            out = out.replace(/(href|src)\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, '$1="#"');
            return out;
        },
        resetUploadForm() {
            this.uploadFileList = [];
        },
        doDelete(doc) {
            this.$http
                .post(this.$store.state.base_url + "/document/d", { id: doc.id })
                .then((res) => {
                    if (res.data.status) {
                        this.documents = this.documents.filter((d) => d.id !== doc.id);
                        this.$message.success("Document deleted.");
                    }
                });
        },
        fmtDate(val) {
            if (!val) return "";
            const m = moment(val);
            return m.isValid() ? m.format("DD MMM YYYY") : val;
        },
    },
    mounted() {
        this.fetchDocuments();
    },
};
</script>

<style scoped>
.documents-share {
    padding: 4px;
}

.doc-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.doc-subtitle {
    color: rgba(0, 0, 0, 0.45);
    margin-top: 4px;
}

.doc-search-hint {
    display: block;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 8px;
}

.eml-preview {
    width: 100%;
    min-height: 100%;
}

.eml-viewer {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 16px;
}

.eml-subject {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 12px;
}

.eml-header {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    margin-bottom: 16px;
}

.eml-row {
    display: flex;
    border-bottom: 1px solid #f5f5f5;
}

.eml-row:last-child {
    border-bottom: 0;
}

.eml-key {
    width: 90px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
    background: #fafafa;
    padding: 6px 8px;
}

.eml-val {
    flex: 1;
    padding: 6px 8px;
    word-break: break-word;
}

.eml-body {
    margin: 0;
    padding: 10px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    background: #fff;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.5;
}

.eml-body-frame {
    width: 100%;
    min-height: 300px;
    border: 0;
}
</style>
